import React, { useState } from 'react';
import { FaDatabase, FaCalendarAlt, FaUser, FaDesktop, FaFileAlt, FaBell, FaCheckCircle } from 'react-icons/fa';

const DataBreachForm = ({ setStep, onSubmit }) => {
  const [formData, setFormData] = useState({
    dateDiscovered: '',
    timeDiscovered: '',
    discoveredBy: '',
    affectedSystems: [],
    estimatedPatientCount: '',
    dataTypes: [],
    breachMethod: '',
    incidentDescription: '',
    immediateActions: '',
    containmentStatus: '',
    notificationsRequired: [],
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: '',
    priority: 'medium'
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const systemOptions = [
    'Practice Management System',
    'Electronic Health Records',
    'Billing System',
    'Email Server',
    'File Server',
    'Backup Systems',
    'Cloud Storage',
    'Other'
  ];

  const dataTypeOptions = [
    'Patient Names',
    'Social Security Numbers',
    'Medical Record Numbers',
    'Health Insurance Info',
    'Medical History',
    'Billing Information',
    'Contact Information'
  ];

  const breachMethodOptions = [
    'IT Incident',
    'Access Event',
    'Device Event',
    'Device Event',
    'Improper Disposal',
    'Other'
  ];

  const notificationOptions = [
    'Practice Management',
    'IT Department',
    'Legal Counsel',
    'Insurance Provider',
    'Patients (if >500)',
    'HHS/OCR (if >500)',
    'Law Enforcement'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleMultiSelectChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked 
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.dateDiscovered) newErrors.dateDiscovered = 'Date discovered is required';
    if (!formData.timeDiscovered) newErrors.timeDiscovered = 'Time discovered is required';
    if (!formData.discoveredBy.trim()) newErrors.discoveredBy = 'Discovered by is required';
    if (!formData.incidentDescription.trim()) newErrors.incidentDescription = 'Event description is required';
    if (!formData.reportedBy.trim()) newErrors.reportedBy = 'Reporter name is required';
    if (!formData.reportedByEmail.trim()) newErrors.reportedByEmail = 'Reporter email is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      await onSubmit({
        ...formData,
        formType: 'ITBreach',
        subType: 'DataBreach',
        submittedAt: new Date(),
        severity: formData.priority
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-violet-100">
            <FaDatabase className="size-8 text-violet-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Data Event Report
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report access to patient data or PHI that requires review
          </p>
          <div className={`mt-4 inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ${getPriorityColor(formData.priority)}`}>
            Priority: {formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
          </div>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Event Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Event Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When and how the event was discovered
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Date Discovered <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateDiscovered"
                        name="dateDiscovered"
                        type="date"
                        value={formData.dateDiscovered}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.dateDiscovered ? 'outline-red-500' : ''}`}
                      />
                      {errors.dateDiscovered && <p className="mt-1 text-sm text-red-600">{errors.dateDiscovered}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Time Discovered <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="timeDiscovered"
                        name="timeDiscovered"
                        type="time"
                        value={formData.timeDiscovered}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.timeDiscovered ? 'outline-red-500' : ''}`}
                      />
                      {errors.timeDiscovered && <p className="mt-1 text-sm text-red-600">{errors.timeDiscovered}</p>}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="discoveredBy" className="block text-sm/6 font-medium text-gray-900">
                      Discovered By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="discoveredBy"
                        name="discoveredBy"
                        type="text"
                        value={formData.discoveredBy}
                        onChange={handleInputChange}
                        placeholder="Name and title of person who discovered the event"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.discoveredBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.discoveredBy && <p className="mt-1 text-sm text-red-600">{errors.discoveredBy}</p>}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm/6 font-medium text-gray-900 mb-4">Priority Level</label>
                    <div className="flex gap-4">
                      {['low', 'medium', 'high', 'critical'].map(priority => (
                        <div key={priority} className="flex items-center">
                          <input
                            type="radio"
                            name="priority"
                            id={`priority-${priority}`}
                            value={priority}
                            checked={formData.priority === priority}
                            onChange={handleInputChange}
                            className="h-4 w-4 border-gray-300 text-violet-600 focus:ring-violet-600"
                          />
                          <label htmlFor={`priority-${priority}`} className="ml-2 block text-sm font-medium text-gray-900 capitalize">
                            {priority}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Affected Systems and Data */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaDesktop className="size-5 text-gray-400" />
                  Affected Systems & Data
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Systems and data potentially affected
                </p>

                <div className="mt-10">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">Affected Systems</label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {systemOptions.map(system => (
                      <div key={system} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              value={system}
                              checked={formData.affectedSystems.includes(system)}
                              onChange={(e) => handleMultiSelectChange(e, 'affectedSystems')}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:border-gray-300 disabled:bg-gray-100"
                            />
                            <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                              <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{system}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <label htmlFor="estimatedPatientCount" className="block text-sm/6 font-medium text-gray-900">
                    Estimated Number of Affected Patients
                  </label>
                  <div className="mt-2">
                    <input
                      id="estimatedPatientCount"
                      name="estimatedPatientCount"
                      type="number"
                      value={formData.estimatedPatientCount}
                      onChange={handleInputChange}
                      placeholder="Enter number (leave blank if unknown)"
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">Types of Data Potentially Affected</label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {dataTypeOptions.map(dataType => (
                      <div key={dataType} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              value={dataType}
                              checked={formData.dataTypes.includes(dataType)}
                              onChange={(e) => handleMultiSelectChange(e, 'dataTypes')}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                            />
                            <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                              <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{dataType}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <label htmlFor="breachMethod" className="block text-sm/6 font-medium text-gray-900">
                    How Event Occurred
                  </label>
                  <div className="mt-2">
                    <select
                      id="breachMethod"
                      name="breachMethod"
                      value={formData.breachMethod}
                      onChange={handleInputChange}
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                    >
                      <option value="">Select method...</option>
                      {breachMethodOptions.map(method => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Event Description */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFileAlt className="size-5 text-gray-400" />
                  Event Description
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Detailed description of the event and response
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="incidentDescription" className="block text-sm/6 font-medium text-gray-900">
                      Detailed Description <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="incidentDescription"
                        name="incidentDescription"
                        rows={4}
                        value={formData.incidentDescription}
                        onChange={handleInputChange}
                        placeholder="Describe what happened, how it was discovered, and the timeline of events..."
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.incidentDescription ? 'outline-red-500' : ''}`}
                      />
                      {errors.incidentDescription && <p className="mt-1 text-sm text-red-600">{errors.incidentDescription}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="immediateActions" className="block text-sm/6 font-medium text-gray-900">
                      Immediate Actions Taken
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="immediateActions"
                        name="immediateActions"
                        rows={3}
                        value={formData.immediateActions}
                        onChange={handleInputChange}
                        placeholder="Describe any immediate steps taken to address the event..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="containmentStatus" className="block text-sm/6 font-medium text-gray-900">
                      Containment Status
                    </label>
                    <div className="mt-2">
                      <select
                        id="containmentStatus"
                        name="containmentStatus"
                        value={formData.containmentStatus}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option value="">Select status...</option>
                        <option value="contained">Event Contained</option>
                        <option value="ongoing">Event Ongoing</option>
                        <option value="unknown">Status Unknown</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notifications */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaBell className="size-5 text-gray-400" />
                  Required Notifications
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select required notifications
                </p>

                <div className="mt-10">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">Notifications Required</label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {notificationOptions.map(notification => (
                      <div key={notification} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              value={notification}
                              checked={formData.notificationsRequired.includes(notification)}
                              onChange={(e) => handleMultiSelectChange(e, 'notificationsRequired')}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                            />
                            <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                              <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{notification}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Reporter Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Reporter Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Your contact information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="reportedBy" className="block text-sm/6 font-medium text-gray-900">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedBy"
                        name="reportedBy"
                        type="text"
                        value={formData.reportedBy}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedBy && <p className="mt-1 text-sm text-red-600">{errors.reportedBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reportedByTitle" className="block text-sm/6 font-medium text-gray-900">
                      Your Title
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByTitle"
                        name="reportedByTitle"
                        type="text"
                        value={formData.reportedByTitle}
                        onChange={handleInputChange}
                        placeholder="Your job title"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="reportedByEmail" className="block text-sm/6 font-medium text-gray-900">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByEmail"
                        name="reportedByEmail"
                        type="email"
                        value={formData.reportedByEmail}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByEmail ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByEmail && <p className="mt-1 text-sm text-red-600">{errors.reportedByEmail}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-x-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DataBreachForm;
